import type { ConfigPayload } from './template-form.util';
import { HOST_V1_CONFIG_TYPE_ID, INTERCEPT_V1_CONFIG_TYPE_ID, MicrosegmentConfigTypeIds } from './microsegment-mapping.util';

export type SimpleServiceProtocol = 'tcp' | 'udp' | 'both';

/**
 * The editable ingredients of a simple service with an explicit, fixed host - a single intercept
 * address/port the client reaches, and a single host address/port the bind identity serves (which
 * may differ from the intercept). Mirrors the field set of the legacy ziti-console-lib
 * simple-service form.
 *
 * SDK-only sides (a dial client or bind host that has no intercept / host config) are not modeled
 * here: whether to attach each side's config is a composition decision owned by
 * `SimpleServiceCreateService`, so this stays a pure description of the addresses.
 */
export interface SimpleServiceDraft {
    serviceName: string;
    /** Accessing (intercept / dial) side. */
    interceptAddress: string;
    interceptPort?: number;
    /** Hosting (host / bind) side. */
    hostAddress: string;
    hostPort?: number;
    hostProtocol: SimpleServiceProtocol;
}

/** Expand the host protocol choice into the config's allowedProtocols list. */
function allowedProtocols(protocol: SimpleServiceProtocol): string[] {
    return protocol === 'both' ? ['tcp', 'udp'] : [protocol];
}

/**
 * Build the intercept.v1 + host.v1 config payloads for a simple service with a fixed host. Both
 * configs are always produced; dropping a side for an SDK-only client or host is handled by
 * `SimpleServiceCreateService`. Config names follow the shared `<serviceName>-<kind>-config`
 * convention so simple and hand-built services stay interchangeable.
 */
export function buildSimpleServiceConfigPayloads(
    draft: SimpleServiceDraft,
    ids: MicrosegmentConfigTypeIds = {}
): ConfigPayload[] {
    const interceptConfigTypeId = ids.interceptConfigTypeId || INTERCEPT_V1_CONFIG_TYPE_ID;
    const hostConfigTypeId = ids.hostConfigTypeId || HOST_V1_CONFIG_TYPE_ID;

    return [
        {
            configTypeId: interceptConfigTypeId,
            name: `${draft.serviceName}-intercept-config`,
            data: {
                addresses: [draft.interceptAddress],
                portRanges: draft.interceptPort != null ? [{ low: draft.interceptPort, high: draft.interceptPort }] : [],
                protocols: ['tcp', 'udp'],
            },
        },
        {
            configTypeId: hostConfigTypeId,
            name: `${draft.serviceName}-host-config`,
            data: {
                address: draft.hostAddress,
                port: draft.hostPort,
                forwardProtocol: true,
                allowedProtocols: allowedProtocols(draft.hostProtocol),
            },
        },
    ];
}
